import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Client, ClientSearchParams } from '../../services/clientApi';

// State interfaces
export interface ClientState {
  // Data
  items: Client[];
  selectedItem: Client | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  formData: {
    Name: string;
    Description: string;
    Active: boolean;
  };
  
  // Validation
  validationErrors: string[];
  
  // Search and Filter
  searchParams: ClientSearchParams;
  
  // Pagination
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
  };
  
  // Bulk Operations
  selectedIds: number[];
  bulkOperationLoading: boolean;
  
  // Success Messages
  successMessage: string;
}

const initialState: ClientState = {
  // Data
  items: [],
  selectedItem: null,
  
  // UI State
  loading: false,
  error: null,
  
  // Form State
  formData: {
    Name: '',
    Description: '',
    Active: true,
  },
  
  // Validation
  validationErrors: [],
  
  // Search and Filter
  searchParams: {
    page: 1,
    pageSize: 10,
    search: '',
    active: undefined,
    sortBy: 'Name',
    sortOrder: 'asc',
  },
  
  // Pagination
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  
  // Statistics
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
  },
  
  // Bulk Operations
  selectedIds: [],
  bulkOperationLoading: false,
  
  // Success Messages
  successMessage: '',
};

const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data management
    setItems: (state, action: PayloadAction<Client[]>) => {
      state.items = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<Client | null>) => {
      state.selectedItem = action.payload;
    },
    addItem: (state, action: PayloadAction<Client>) => {
      state.items.unshift(action.payload);
    },
    updateItem: (state, action: PayloadAction<Client>) => {
      const index = state.items.findIndex(item => item.Id === action.payload.Id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.Id !== action.payload);
    },
    
    // Form management
    setFormData: (state, action: PayloadAction<Partial<ClientState['formData']>>) => {
      state.formData = { ...state.formData, ...action.payload };
      // Clear validation errors when form data changes
      if (state.validationErrors.length > 0) {
        state.validationErrors = [];
      }
    },
    resetFormData: (state) => {
      state.formData = initialState.formData;
      state.validationErrors = [];
    },
    
    // Validation
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    
    // Search and filter
    setSearchParams: (state, action: PayloadAction<Partial<ClientSearchParams>>) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    resetSearchParams: (state) => {
      state.searchParams = initialState.searchParams;
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<ClientState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<ClientState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // Bulk operations
    setSelectedIds: (state, action: PayloadAction<number[]>) => {
      state.selectedIds = action.payload;
    },
    addSelectedId: (state, action: PayloadAction<number>) => {
      if (!state.selectedIds.includes(action.payload)) {
        state.selectedIds.push(action.payload);
      }
    },
    removeSelectedId: (state, action: PayloadAction<number>) => {
      state.selectedIds = state.selectedIds.filter(id => id !== action.payload);
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    setBulkOperationLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkOperationLoading = action.payload;
    },
    
    // Success messages
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = '';
    },
    
    // Reset state
    resetClientState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setFormData,
  resetFormData,
  setValidationErrors,
  clearValidationErrors,
  setSearchParams,
  resetSearchParams,
  setPagination,
  setStatistics,
  setSelectedIds,
  addSelectedId,
  removeSelectedId,
  clearSelectedIds,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  resetClientState,
} = clientSlice.actions;

// Saga action types
export const FETCH_CLIENTS = 'client/fetchClients';
export const FETCH_CLIENT_BY_ID = 'client/fetchClientById';
export const CREATE_CLIENT = 'client/createClient';
export const UPDATE_CLIENT = 'client/updateClient';
export const DELETE_CLIENT = 'client/deleteClient';
export const SEARCH_CLIENTS = 'client/searchClients';
export const FETCH_STATISTICS = 'client/fetchStatistics';
export const BULK_ACTIVATE = 'client/bulkActivate';
export const BULK_DEACTIVATE = 'client/bulkDeactivate';
export const BULK_DELETE = 'client/bulkDelete';
export const EXPORT_TO_CSV = 'client/exportToCSV';

// Saga action creators
export const fetchClients = (params?: ClientSearchParams) => ({ type: FETCH_CLIENTS, payload: params });
export const fetchClientById = (id: number) => ({ type: FETCH_CLIENT_BY_ID, payload: id });
export const createClient = (client: Omit<Client, 'Id'>) => ({ type: CREATE_CLIENT, payload: client });
export const updateClient = (id: number, client: Partial<Client>) => ({ type: UPDATE_CLIENT, payload: { id, client } });
export const deleteClient = (id: number) => ({ type: DELETE_CLIENT, payload: id });
export const searchClients = (params: ClientSearchParams) => ({ type: SEARCH_CLIENTS, payload: params });
export const fetchStatistics = () => ({ type: FETCH_STATISTICS });
export const bulkActivate = (ids: number[]) => ({ type: BULK_ACTIVATE, payload: ids });
export const bulkDeactivate = (ids: number[]) => ({ type: BULK_DEACTIVATE, payload: ids });
export const bulkDelete = (ids: number[]) => ({ type: BULK_DELETE, payload: ids });
export const exportToCSV = (params?: ClientSearchParams) => ({ type: EXPORT_TO_CSV, payload: params });

export default clientSlice.reducer; 